import WebSocketManager from './websocket-manager';
import type { WebSocketManagerConfig } from './types';
import { apidata } from '../../out/prices';
import { WebSocketClientType } from '../types';

export type USPriceCallback = (message: apidata.prices.PriceData) => void;

interface ISubscriptions {
  path: string;
  callback: USPriceCallback;
  subId: string;
}

// Default protobuf decoder for US prices (same format as Indian prices)
const defaultProtobufDecoder = (data: Uint8Array): apidata.prices.PriceData => {
  const PriceDataClass = apidata.prices.PriceData;
  return PriceDataClass.decode(data) as unknown as apidata.prices.PriceData;
};

export interface USPricesWebSocketManagerConfig {
  websocketUrl: string;
  socketId: WebSocketClientType;
  protobufDecoder?: (data: Uint8Array) => apidata.prices.PriceData;
  websocketConfig: WebSocketManagerConfig;
}

class USPricesWebSocketManager extends WebSocketManager {
  private static usPricesInstance: USPricesWebSocketManager | null = null;
  private usPricesWebSocketUrl: string;
  private static setConnectionCallback: (
    ws: WebSocket | null,
    client: string,
  ) => void = () => {};
  private socketId: WebSocketClientType = WebSocketClientType.US_PRICES;
  private subscriptions: Map<string, ISubscriptions[]> = new Map();
  private connectionStartTime: number = 0;
  private isConnectionInProgress: boolean = false;
  private lastOnMessageError: string = '';
  private protobufDecoder: (data: Uint8Array) => apidata.prices.PriceData;

  private constructor(config: USPricesWebSocketManagerConfig) {
    super(config.websocketConfig);
    this.usPricesWebSocketUrl = config.websocketUrl;
    this.socketId = config.socketId;
    this.protobufDecoder = config.protobufDecoder || defaultProtobufDecoder;
  }

  static getUSPricesInstance(
    config?: USPricesWebSocketManagerConfig,
  ): USPricesWebSocketManager {
    if (!this.usPricesInstance) {
      if (!config) {
        throw new Error('Config is required for first initialization');
      }
      this.usPricesInstance = new USPricesWebSocketManager(config);
    }
    return this.usPricesInstance;
  }

  static initializeUSPricesSocketManager(
    config: USPricesWebSocketManagerConfig,
    setConnectionCallback: (ws: WebSocket | null, client: string) => void,
    hashToken: string | null = null,
  ): void {
    console.log('[USPricesWebSocketManager] Initializing socket manager', {
      socketId: config.socketId,
      hasHashToken: !!hashToken,
    });
    if (!this.usPricesInstance) {
      this.usPricesInstance = new USPricesWebSocketManager(config);
    }
    super.initialize(hashToken);
    USPricesWebSocketManager.setConnectionCallback = setConnectionCallback;
  }

  static getUSPricesWSInstance(): USPricesWebSocketManager | null {
    if (!this.usPricesInstance) {
      return null;
    }
    return this.usPricesInstance;
  }

  async initializeUSPriceConnection(): Promise<void> {
    try {
      if (this.isConnectionInProgress) {
        console.log(
          '[USPricesWebSocketManager] Connection already in progress',
        );
        return;
      }
      this.isConnectionInProgress = true;
      console.log('[USPricesWebSocketManager] Initializing connection', {
        socketId: this.socketId,
        url: this.usPricesWebSocketUrl,
      });
      await super.initializeConnection(
        this.socketId,
        this.usPricesWebSocketUrl,
        this.setupEventListeners.bind(this),
      );
      this.connectionStartTime = Date.now();
    } catch (error: unknown) {
      console.error(
        '[USPricesWebSocketManager] Error initializing US price connection:',
        error,
      );
    } finally {
      this.isConnectionInProgress = false;
    }
  }

  async disconnectUSPriceConnection(): Promise<void> {
    void super.disconnect(this.socketId);
  }

  getUSPricesConnection(): WebSocket | null {
    return super.getSocket(this.socketId);
  }

  private setupEventListeners() {
    const socket = this.getUSPricesConnection();

    if (!socket) {
      return;
    }

    socket.onopen = () => {
      console.log(`WebSocket connected --> ${this.socketId}`);

      // Ensure socket is in the static Map (may have been cleared by cleanup/HMR)
      super.ensureSocketInMap(this.socketId, socket);

      USPricesWebSocketManager.setConnectionCallback(socket, this.socketId);

      super.resetReconnectAttempts(this.socketId);

      const connectionTime = super.getConnectionCreationTime(this.socketId);
    };

    socket.onclose = (event) => {
      console.log(`WebSocket disconnected --> ${this.socketId}`, event);

      USPricesWebSocketManager.setConnectionCallback(null, this.socketId);

      if (!event.wasClean) {
        super.handleReconnection(this.socketId, 'onclose');
      }
    };

    socket.onmessage = async (event: MessageEvent) => {
      try {
        const arrayBuffer = await (event.data instanceof Blob
          ? event.data.arrayBuffer()
          : event.data);

        const uint8Array = new Uint8Array(arrayBuffer);
        const decodedMessage = this.protobufDecoder(uint8Array);

        this.subscriptions.forEach((subscriptions, path) => {
          const subPath = decodedMessage.subPath;

          if (!subPath) {
            console.error(
              '[USPricesWebSocketManager] No subPath found in the message',
              decodedMessage,
            );
            return;
          }

          if (path === subPath) {
            subscriptions.forEach((sub) => {
              try {
                sub.callback(decodedMessage);
              } catch (error: unknown) {
                console.error(
                  '[USPricesWebSocketManager] Error processing WebSocket message',
                  error,
                );
              }
            });
          }
        });

        this.lastOnMessageError = '';
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        console.error(
          `[USPricesWebSocketManager] Error processing WebSocket message --> ${this.socketId}`,
          errorMessage,
        );
        if (this.lastOnMessageError !== errorMessage) {
          this.lastOnMessageError = errorMessage;
        }
      }
    };

    socket.onerror = (error) => {
      console.error(`WebSocket error --> ${this.socketId}`, error);

      super.handleReconnection(this.socketId, 'onerror');
    };
  }

  subscribe(path: string, callback: USPriceCallback): () => void {
    try {
      const subId = `sub-${path}-us-prices-${Math.random()
        .toString(8)
        .slice(2, 11)}`;
      const socket = this.getUSPricesConnection();
      if (!socket) {
        console.log(
          '[USPricesWebSocketManager] US Prices connection not initialized',
        );
        throw new Error('US Prices connection not initialized');
      }

      const existingSubscriptions = this.subscriptions.get(path) ?? [];

      if (!existingSubscriptions.length) {
        if (socket.readyState == WebSocket.OPEN) {
          const subscribeMessage = {
            action: 'SUB',
            mode: 'P',
            entities: [`${path}`],
          };
          socket.send(JSON.stringify(subscribeMessage));
        }
      }

      this.subscriptions.set(path, [
        ...existingSubscriptions,
        { path, callback, subId },
      ]);

      // Return unsubscribe function
      return () => {
        const subs = this.subscriptions.get(path);
        if (subs) {
          const filteredSubs = subs.filter((sub) => sub.subId !== subId);

          if (filteredSubs.length === 0) {
            if (socket.readyState === WebSocket.OPEN) {
              const unsubscribeMessage = {
                action: 'UNSUB',
                mode: 'P',
                entities: [`${path}`],
              };
              socket.send(JSON.stringify(unsubscribeMessage));
            }
            this.subscriptions.delete(path);
          } else {
            this.subscriptions.set(path, filteredSubs);
          }
        }
      };
    } catch (err: unknown) {
      console.error(
        '[USPricesWebSocketManager] Error subscribing to US price',
        err,
      );

      return () => {};
    }
  }

  isSubscribed(path: string): boolean {
    return this.subscriptions.has(path);
  }
}

export default USPricesWebSocketManager;
